import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, filter, withLatestFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { Resolutions } from '@core/domain/state/core.state';
import { getCmsConfigValue, getResolution, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { PublicUser } from '@features/user';
import { ContactStatus } from '@pages/contact/contact.status';
import { getUserSelector } from '@shared/user/store';

@Injectable()
export class WhatsappService {
    constructor(
        private readonly store: Store,
    ) {}

    private readonly whatsappAvailable$ = this.store.select(getCmsConfigValue('channels_not_available')).pipe(
        filter(channelsNotAvailable => !!channelsNotAvailable),
        map((channelsNotAvailable) => {
            const whatsappNotAvailable = channelsNotAvailable.contact.find(channel => channel === 'whatsapp');
            return channelsNotAvailable?.active && whatsappNotAvailable;
        })
    );

    public whatsAppInfo$ = combineLatest([
        this.store.select(getCmsConfigValue('whatsapp')),
        this.store.select(getResolution),
        this.store.select(getUserSelector),
    ]).pipe(
        map(([config, resolution, user]) => {
            const link = WhatsappService.getWhatsappLink(
                config.target,
                config.phoneNumber,
                resolution,
                user
            );
            return { ...config, link };
        })
    );

    public whatsappEnabled$ = this.whatsAppInfo$.pipe(
        withLatestFrom(this.store.select(getStoreAndLanguage), this.store.select(getUserSelector)),
        map(([config, storeAndLanguage, user]) => {
            if (!config?.enabled || config?.target !== 'ALL' && !user?.is360) {
                return false;
            }
            return config?.languages?.includes(+storeAndLanguage?.languageId);
        })
    );

    public whatsappStatus$ = combineLatest([
        this.whatsappAvailable$,
        this.whatsAppInfo$,
    ]).pipe(
        map(([channelsAvailabilityWhatsapp, whatsAppConfig]) => {
            if (!channelsAvailabilityWhatsapp && whatsAppConfig?.enabled) {
                return ContactStatus.ENABLED;
            }
            if (!channelsAvailabilityWhatsapp && !whatsAppConfig?.enabled) {
                return ContactStatus.UNSET;
            }
            return ContactStatus.DISABLED;
        })
    );

    private static getWhatsappLink(target: string, phone: string, resolution: Resolutions, user: PublicUser) {
        let link: string | false;
        if (resolution === Resolutions.MOBILE) {
            link = `https://wa.me/${phone}`;
        } else if (target === 'FEEL' && !user.is360) {
            link = false;
        } else {
            link = `https://web.whatsapp.com/send?phone=${phone}`;
        }
        return link;
    }
}
